/**
 * File: src/state/modal-state.js
 * Mô tả: Quản lý trạng thái hiển thị của Modal chi tiết game và Lightbox xem ảnh screenshot.
 * Liên quan: src/components/game-detail-modal.js, src/components/lightbox.js, src/utils/accessibility.js
 */

import { lockScroll, unlockScroll, saveFocus, restoreFocus } from '../utils/accessibility.js';

/**
 * @typedef {Object} ModalState
 * @property {import('../data/games.js').Game|null} activeGame - Game đang được chọn xem chi tiết
 * @property {boolean} isModalOpen - Trạng thái mở modal chi tiết game
 * @property {string[]} activeScreenshots - Danh sách ảnh của game đang xem trong Lightbox
 * @property {number} activeScreenshotIndex - Chỉ số ảnh đang chọn trong Lightbox
 * @property {boolean} isLightboxOpen - Trạng thái mở Lightbox xem ảnh
 */

/** @type {ModalState} */
const state = {
  activeGame: null,
  isModalOpen: false,
  activeScreenshots: [],
  activeScreenshotIndex: 0,
  isLightboxOpen: false,
};

/** @type {Set<Function>} */
const listeners = new Set();

/**
 * Đăng ký lắng nghe sự thay đổi trạng thái state
 * @param {Function} callback 
 * @returns {Function} Hàm hủy đăng ký
 */
export const subscribeModalState = (callback) => {
  listeners.add(callback);
  return () => listeners.delete(callback);
};

const notifyListeners = () => {
  listeners.forEach((listener) => {
    try {
      listener({ ...state });
    } catch (error) {
      console.error('Lỗi khi thực thi listener modalState:', error);
    }
  });
};

/**
 * Lấy trạng thái hiện tại
 * @returns {ModalState}
 */
export const getModalState = () => ({ ...state });

/**
 * Mở Modal chi tiết game
 * @param {import('../data/games.js').Game} game 
 */
export const openGameModal = (game) => {
  if (!game) return;
  saveFocus();
  state.activeGame = game;
  state.isModalOpen = true;
  lockScroll();
  notifyListeners();
};

/**
 * Đóng Modal chi tiết game
 */
export const closeGameModal = () => {
  if (!state.isModalOpen) return;
  state.isModalOpen = false;
  state.activeGame = null;
  if (!state.isLightboxOpen) {
    unlockScroll();
    restoreFocus();
  }
  notifyListeners();
};

/**
 * Mở Lightbox xem ảnh screenshot phóng to
 * @param {string[]} screenshots 
 * @param {number} initialIndex 
 */
export const openLightbox = (screenshots, initialIndex = 0) => {
  if (!screenshots || screenshots.length === 0) return;
  saveFocus();
  state.activeScreenshots = screenshots;
  state.activeScreenshotIndex = initialIndex;
  state.isLightboxOpen = true;
  lockScroll();
  notifyListeners();
};

/**
 * Đóng Lightbox
 */
export const closeLightbox = () => {
  if (!state.isLightboxOpen) return;
  state.isLightboxOpen = false;
  state.activeScreenshots = [];
  state.activeScreenshotIndex = 0;
  if (!state.isModalOpen) {
    unlockScroll();
    restoreFocus();
  }
  notifyListeners();
};

/**
 * Chuyển sang ảnh tiếp theo trong Lightbox
 */
export const nextScreenshot = () => {
  if (!state.isLightboxOpen || state.activeScreenshots.length === 0) return;
  state.activeScreenshotIndex = (state.activeScreenshotIndex + 1) % state.activeScreenshots.length;
  notifyListeners();
};

/**
 * Chuyển sang ảnh trước đó trong Lightbox
 */
export const prevScreenshot = () => {
  if (!state.isLightboxOpen || state.activeScreenshots.length === 0) return;
  state.activeScreenshotIndex =
    (state.activeScreenshotIndex - 1 + state.activeScreenshots.length) % state.activeScreenshots.length;
  notifyListeners();
};
